import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Logo = () => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <div style={{ width: 30, height: 30, background: '#00d55b', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
    </div>
    <div>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#fff', lineHeight: 1 }}><span style={{ color: '#00d55b' }}>MS</span>Chat</div>
      <div style={{ fontSize: 8, color: '#444', letterSpacing: '0.06em', textTransform: 'uppercase' }}>by MS Discoveries</div>
    </div>
  </div>
);

export default function DashLayout({ children, navItems, role }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => { logout(); navigate('/login'); };

  const SidebarContent = ({ isMobile = false }) => (
    <>
      {/* Logo */}
      <div style={{ padding: collapsed && !isMobile ? '14px 12px' : '14px 18px', borderBottom: '1px solid #1e1e1e', height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
        {(!collapsed || isMobile) && <Logo />}
        {isMobile ? (
          <button onClick={() => setMobileOpen(false)}
            style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 20, padding: 4 }}>
            ✕
          </button>
        ) : (
          <button onClick={() => setCollapsed(!collapsed)}
            style={{ background: 'none', border: 'none', color: '#444', cursor: 'pointer', padding: 4, fontSize: 14, marginLeft: collapsed ? 'auto' : 0, flexShrink: 0 }}>
            {collapsed ? '→' : '←'}
          </button>
        )}
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {navItems.map(item => {
          const active = loc.pathname === item.to;
          return (
            <Link key={item.to} to={item.to} title={collapsed && !isMobile ? item.label : ''}
              onClick={() => setMobileOpen(false)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, color: active ? '#00d55b' : '#555', background: active ? 'rgba(0,213,91,0.08)' : 'transparent', textDecoration: 'none', fontSize: 13, fontWeight: active ? 600 : 400, transition: 'all 0.15s', whiteSpace: 'nowrap', overflow: 'hidden', borderLeft: active ? '2px solid #00d55b' : '2px solid transparent' }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#111'; e.currentTarget.style.color = '#fff'; } }}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; } }}>
              <span style={{ fontSize: 15, flexShrink: 0 }}>{item.icon}</span>
              {(!collapsed || isMobile) && <span>{item.label}</span>}
              {(!collapsed || isMobile) && item.badge && <span style={{ marginLeft: 'auto', background: '#00d55b', color: '#000', fontSize: 10, padding: '1px 7px', borderRadius: 10, fontWeight: 700 }}>{item.badge}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User + logout */}
      <div style={{ padding: '10px 8px', borderTop: '1px solid #1e1e1e', flexShrink: 0 }}>
        {(!collapsed || isMobile) && (
          <div style={{ padding: '8px 12px', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || user?.company_name || user?.email}</div>
            <div style={{ fontSize: 11, color: '#444', marginTop: 2 }}>{role}</div>
          </div>
        )}
        <button onClick={handleLogout} title="Logout"
          style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8, color: '#555', background: 'transparent', border: 'none', cursor: 'pointer', width: '100%', fontSize: 13, transition: 'all 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#1a0000'; e.currentTarget.style.color = '#ff4444'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#555'; }}>
          <span style={{ fontSize: 15 }}>🚪</span>
          {(!collapsed || isMobile) && 'Logout'}
        </button>
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#000', overflow: 'hidden' }}>

      {/* ── MOBILE: Top bar with hamburger ── */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, height: 56, background: '#0d0d0d', borderBottom: '1px solid #1e1e1e', zIndex: 1000, alignItems: 'center', justifyContent: 'space-between', padding: '0 16px' }}
        className="mobile-topbar">
        <button onClick={() => setMobileOpen(true)}
          style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', padding: 4, lineHeight: 1 }}>
          ☰
        </button>
        <Logo />
        <div style={{ width: 32 }} />
      </div>

      {/* ── MOBILE: Overlay ── */}
      {mobileOpen && (
        <div onClick={() => setMobileOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1001 }} />
      )}

      {/* ── MOBILE: Slide-in sidebar ── */}
      <div style={{ position: 'fixed', top: 0, left: mobileOpen ? 0 : '-260px', width: 240, height: '100vh', background: '#0d0d0d', borderRight: '1px solid #1e1e1e', zIndex: 1002, display: 'flex', flexDirection: 'column', transition: 'left 0.25s ease' }}
        className="mobile-sidebar">
        <SidebarContent isMobile={true} />
      </div>

      {/* ── DESKTOP: Sidebar ── */}
      <div style={{ width: collapsed ? 56 : 210, background: '#0d0d0d', borderRight: '1px solid #1e1e1e', display: 'flex', flexDirection: 'column', transition: 'width 0.2s', flexShrink: 0 }}
        className="desktop-sidebar">
        <SidebarContent />
      </div>

      {/* ── Main content ── */}
      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}
        className="dash-main">
        {children}
      </div>

      {/* ── Responsive CSS ── */}
      <style>{`
        .mobile-topbar { display: none; }
        .mobile-sidebar { display: flex; }
        @media (max-width: 768px) {
          .desktop-sidebar { display: none !important; }
          .mobile-topbar { display: flex !important; }
          .dash-main { padding-top: 56px !important; }
          .mobile-sidebar { display: flex !important; }
        }
        @media (min-width: 769px) {
          .mobile-sidebar { left: -260px !important; }
          .mobile-topbar { display: none !important; }
        }
      `}</style>
    </div>
  );
}